import { Container, IDestroyOptions, Point, Sprite, Texture } from "pixi.js";
import { AnimationController } from "../AnimationController";
import { Event } from "event-system";
import { gsap } from "gsap";
import { SlowMo } from "gsap/EasePack";

export class PiggyRewardCollectAnimationView extends Container {
    private static readonly MOVE_DURATION: number = 1.5/*s*/;

    private readonly animationController: AnimationController;

    private tokenRewardTexture: Texture;
    private couponRewardTexture: Texture;
    private readonly tokenCollectPoint: Point;
    private readonly couponCollectPoint: Point;
    private coinCollectAnimations: Array<gsap.core.Timeline>;

    public get coinsCollected(): Event {
        return this.animationController.animationDone;
    }

    public constructor(
        tokenRewardIcon: Texture,
        couponRewardIcon: Texture,
        tokenCollectPoint: Point,
        couponCollectPoint: Point
    ) {
        super();
        this.animationController = new AnimationController();
        this.tokenRewardTexture = tokenRewardIcon;
        this.couponRewardTexture = couponRewardIcon;
        this.tokenCollectPoint = tokenCollectPoint;
        this.couponCollectPoint = couponCollectPoint;
        this.coinCollectAnimations = [];
    }

    public async startAnimation(): Promise<void> {
        const tokenRewardIcon = new Sprite(this.tokenRewardTexture);
        tokenRewardIcon.width = tokenRewardIcon.height = 200;
        tokenRewardIcon.position.set(179, 1211);
        this.addChild(tokenRewardIcon);
        const couponRewardIcon = new Sprite(this.couponRewardTexture);
        couponRewardIcon.width = 210;
        couponRewardIcon.height = 200;
        couponRewardIcon.position.set(446, 1177);
        this.addChild(couponRewardIcon);
        this.setupTokenIconAnimation(tokenRewardIcon);
        this.setupCouponIconAnimation(couponRewardIcon);
        await this.animationController.startAnimation();
        this.destroy();
    }

    private setupTokenIconAnimation(sprite: Sprite): void {
        const timeline = gsap.timeline();
        this.coinCollectAnimations.push(timeline);
        timeline.to(sprite,
            {
                x: sprite.x - 30,
                y: sprite.y - 750,
                duration: PiggyRewardCollectAnimationView.MOVE_DURATION,
                ease: "power4.out"
            });
        timeline.to(sprite,
            {
                x: this.tokenCollectPoint.x,
                duration: PiggyRewardCollectAnimationView.MOVE_DURATION,
                ease: "power4.in"
            }, "<95%");
        timeline.to(sprite,
            {
                y: this.tokenCollectPoint.y,
                duration: PiggyRewardCollectAnimationView.MOVE_DURATION,
                ease: "power2.in"
            }, "<");
        timeline.to(sprite.scale,
            {
                x: 0.4,
                y: 0.4,
                duration: PiggyRewardCollectAnimationView.MOVE_DURATION,
                ease: "power4.in"
            }, "<");
        const animation = timeline.then(() => {
            sprite.destroy();
            timeline?.kill();
        });
        this.animationController.addAnimation(animation);
    }

    private setupCouponIconAnimation(sprite: Sprite): void {
        const timeline = gsap.timeline();
        this.coinCollectAnimations.push(timeline);
        timeline.to(sprite,
            {
                x: sprite.x - 20,
                y: sprite.y - 600,
                duration: PiggyRewardCollectAnimationView.MOVE_DURATION,
                ease: "power3.out"
            });
        timeline.to(sprite,
            {
                x: this.couponCollectPoint.x,
                duration: PiggyRewardCollectAnimationView.MOVE_DURATION * 0.75,
                ease: "power2.in"
            }, "<95%");
        timeline.to(sprite,
            {
                y: this.couponCollectPoint.y,
                duration: PiggyRewardCollectAnimationView.MOVE_DURATION,
                ease: "power4.in"
            }, "<");
        timeline.to(sprite.scale,
            {
                x: 0.6,
                y: 0.6,
                duration: PiggyRewardCollectAnimationView.MOVE_DURATION * 0.7,
                ease: "power4.in"
            }, "<30%");

        const animation = timeline.then(() => {
            sprite.destroy();
            timeline?.kill();
        });
        this.animationController.addAnimation(animation);
    }

    override destroy(options?: boolean | IDestroyOptions): void {
        this.coinCollectAnimations.forEach(timeLine => timeLine?.kill());
        super.destroy(options);
    }
}
